import { useContext } from "react";
import { Button as AriaButton } from "react-aria-components";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ChevronDown } from "react-feather";
import {
  AuthContext,
  AuthUtils,
  AUTHENTICATED,
} from "@bfc-software/app-toolbox";
import { Menu, MenuListItem } from "@bfc-software/pallet";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
  DEFAULT_LOCALE,
} from "@bfc-software/bfc-web-ui/i18n";

import styles from "./account-menu.module.css";

/**
 * Who you are, and the ways out - switching product, switching company, and
 * signing out.
 *
 * This belongs to the shell, not to any one tool: leaving the app must not
 * require entering a tool first.
 *
 * The design shows a person's name and role here. `userInfo` carries an email
 * and an array of raw role ids, and the roles come back empty, so it shows the
 * email rather than inventing a display name.
 */
export function AccountMenu() {
  const params = useParams({ strict: false });
  const lang = params.lang ?? DEFAULT_LOCALE;
  const navigate = useNavigate();
  const { state, logout } = useContext(AuthContext);
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.portal,
  });

  if (state.status !== AUTHENTICATED) return null;

  const email = state.email;
  const orgs = AuthUtils.getOrgs(state);
  const orgSlug = AuthUtils.getOrgSlug(state, state.orgId);

  return (
    <Menu
      header={
        <div className={styles.identity}>
          <span className={styles.identityEmail}>{email}</span>
        </div>
      }
      triggerNode={
        <AriaButton className={styles.trigger} aria-label={t("account.label")}>
          <span className={styles.avatar} aria-hidden="true">
            {initialsFor(email)}
          </span>
          <span className={styles.email}>{email}</span>
          <ChevronDown size={14} strokeWidth={2} aria-hidden="true" />
        </AriaButton>
      }
    >
      <MenuListItem
        textValue={t("account.switchProduct")}
        onAction={() =>
          navigate({ to: "/$lang/orgs/$org", params: { lang, org: orgSlug } })
        }
      >
        {t("account.switchProduct")}
      </MenuListItem>

      {orgs.length > 1 && (
        <MenuListItem
          textValue={t("account.switchCompany")}
          onAction={() => {
            // Signing out first is what makes this work. A session is bound to
            // one organisation server-side, and the state machine only reaches
            // org select from a fresh sign-in - SELECT_ORG is ignored from any
            // other state. Navigating to login while still authenticated just
            // bounces back to this picker.
            logout();
            navigate({ to: "/$lang/auth/login", params: { lang } });
          }}
        >
          {t("account.switchCompany")}
        </MenuListItem>
      )}

      <MenuListItem
        textValue={t("account.signOut")}
        onAction={() => {
          logout();
          navigate({ to: "/$lang/auth/login", params: { lang } });
        }}
      >
        <span className={styles.signOut}>{t("account.signOut")}</span>
      </MenuListItem>
    </Menu>
  );
}

/** Two letters from the local part of the email - "single-org" gives "SO". */
function initialsFor(email: string): string {
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[.\-_+]/).filter(Boolean);
  const letters =
    parts.length > 1 ? parts[0][0] + parts[1][0] : local.slice(0, 2);
  return letters.toUpperCase();
}
